import type { KeyboardEvent } from 'react';
import styles from './QuantityField.module.scss?module';

interface QuantityFieldProps {
  /** Units, 0 to max. */
  value: number;
  onChange: (value: number) => void;
  /** Name of the flavour, used in the accessible labels. */
  label: string;
  /** Upper bound of the quantity, typed or stepped; MAX_UNITS unless a stock caps it. */
  max?: number;
  /** Id of the input, for an external label. */
  id?: string;
  disabled?: boolean;
}

/** Upper bound of one flavour's quantity when no stock caps it. */
export const MAX_UNITS = 99999;

/**
 * Parses typed input to a whole number of units from 0 to max. Only the leading digits
 * count, so "1.5" is 1, not 15; input without leading digits is 0.
 */
function parseUnits(input: string, max: number): number {
  const digits = /^\d+/.exec(input.trim());
  const units = digits ? Number.parseInt(digits[0], 10) : 0;

  return Math.min(units, max);
}

/**
 * Quantity of one flavour: a numeric field with − and + steppers. Shared by every panel of the
 * booth overlay so all quantity fields look and behave the same.
 */
export default function QuantityField({
  value,
  onChange,
  label,
  max = MAX_UNITS,
  id,
  disabled = false,
}: QuantityFieldProps) {
  const upperBound = Math.max(0, Math.min(max, MAX_UNITS));

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      onChange(Math.min(upperBound, value + 1));
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      onChange(Math.max(0, value - 1));
    }
  };

  return (
    <span className={styles.quantity}>
      <button
        type="button"
        className={styles.stepper}
        onClick={() => onChange(Math.max(0, value - 1))}
        disabled={disabled || value <= 0}
        aria-label={'Decrease quantity of ' + label}
      >
        {'−'}
      </button>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        className={styles.quantityInput}
        value={value > 0 ? String(value) : ''}
        placeholder="0"
        onChange={(event) =>
          onChange(parseUnits(event.target.value, upperBound))
        }
        onKeyDown={handleKeyDown}
        onFocus={(event) => event.currentTarget.select()}
        disabled={disabled}
        aria-label={'Quantity of ' + label}
        autoComplete="off"
      />
      <button
        type="button"
        className={styles.stepper}
        onClick={() => onChange(Math.min(upperBound, value + 1))}
        disabled={disabled || value >= upperBound}
        aria-label={'Increase quantity of ' + label}
      >
        +
      </button>
    </span>
  );
}
